import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

// Import STT provider configuration
import sttProviders from "@/config/stt-providers.json";
import { isRealtimeModel } from "@/utils/realtimeModels";

export default function TranscriberTab() {
  const { control, setValue, watch, getValues } = useFormContext();

  // Get current values immediately
  const initialStt = getValues("stt_provider");
  const initialSttModel = getValues("stt_model");
  const llm_provider = watch("llm_provider");
  const llm_model = watch("llm_model");

  // Check if using a realtime model
  const isUsingRealtimeModel = isRealtimeModel(llm_provider, llm_model);

  // Use state for local tracking
  const [providerState, setProviderState] = useState<string>(
    initialStt ? initialStt.toLowerCase() : "deepgram"
  );

  // Watch these values for reactivity
  const stt_provider = watch("stt_provider");
  const stt_model = watch("stt_model");
  const enable_language_detection = watch("enable_language_detection");
  const providerConfig = sttProviders.providers[providerState];

  // Initialize on mount
  useEffect(() => {
    if (initialStt && !stt_provider) {
      setValue("stt_provider", initialStt, { shouldValidate: true });
    }
    if (initialSttModel && !stt_model) {
      setValue("stt_model", initialSttModel, { shouldValidate: true });
    }
    if (!stt_provider && !initialStt) {
      setValue("stt_provider", "deepgram", { shouldValidate: true });
      setValue("stt_model", "nova-3", { shouldValidate: true });
    }
  }, []);

  // Helper function to get display name for language codes
  const getLanguageDisplayName = (langCode) => {
    const languageNames = {
      'multi': 'Multi-Language (Code-Switching)',
      'en': 'English',
      'en-IN': 'Indian English',
      'hi': 'Hindi',
      'ta': 'Tamil',
      'te': 'Telugu',
      'ml': 'Malayalam',
      'kn': 'Kannada',
      'gu': 'Gujarati',
      'mr': 'Marathi',
      'bn': 'Bengali',
      'or': 'Odia',
      'pa': 'Punjabi',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'zh': 'Chinese',
      'yue': 'Cantonese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ru': 'Russian',
      'ar': 'Arabic',
      'nl': 'Dutch',
      'sv': 'Swedish',
      'tr': 'Turkish'
    };
    return languageNames[langCode] || langCode.toUpperCase();
  };

  // Get languages for current provider
  const getProviderLanguages = () => {
    if (providerConfig?.languages?.length) {
      const uniqueLanguages = Array.from(new Set(providerConfig.languages));
      return uniqueLanguages.map((lang) => ({
        value: lang,
        label: getLanguageDisplayName(lang),
      }));
    }

    return [
      { value: "en", label: "English" },
      { value: "multi", label: "Multi-Language (Code-Switching)" },
      { value: "auto", label: "Auto Detect" },
    ];
  };

  // Get models for current provider
  const getProviderModels = () => {
    const provider = providerConfig;
    if (!provider || !provider.models) return [];
    
    // Handle different model structure formats
    if (Array.isArray(provider.models)) {
      // For providers like Sarvam that have models as direct array
      return provider.models.map(model => ({
        ...model,
        category: 'default'
      }));
    }
    
    // For providers like Deepgram/Groq that have categorized models
    const allModels = [];
    Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
      if (Array.isArray(modelCategory)) {
        modelCategory.forEach(model => {
          allModels.push({
            ...model,
            category: categoryName
          });
        });
      }
    });
    
    return allModels;
  };

  return (
    <div className="space-y-6">
      {/* Show alert if a realtime model is selected */}
      {isUsingRealtimeModel && (
        <Alert className="bg-amber-50 border-amber-200">
          <InfoIcon className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            Transcriber settings are disabled because you're using a realtime model ({llm_model}) that handles speech-to-text natively. This cannot be configured separately.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6" style={{ opacity: isUsingRealtimeModel ? 0.5 : 1, pointerEvents: isUsingRealtimeModel ? 'none' : 'auto' }}>
        <FormField
          control={control}
          name="stt_provider"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Provider</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  setProviderState(value);
                  
                  // Set default model for the provider
                  const provider = sttProviders.providers[value];
                  if (provider && provider.models) {
                    // Handle different model structure formats
                    let models = [];
                    if (Array.isArray(provider.models)) {
                      models = provider.models;
                    } else {
                      // For categorized models, flatten them
                      Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
                        if (Array.isArray(modelCategory)) {
                          models.push(...modelCategory);
                        }
                      });
                    }
                    
                    const defaultModel = models.find(m => m.isDefault) || models[0];
                    if (defaultModel) {
                      setValue("stt_model", defaultModel.id, { shouldValidate: true });
                    }
                  }
                }}
                value={field.value?.toLowerCase() || providerState}
              >
                <FormControl>
                  <SelectTrigger className="bg-muted text-foreground border-input">
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(sttProviders.providers).map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Transcription
            </h3>
            <p className="text-muted-foreground mb-4">
              This section allows you to configure the transcription settings
              for the assistant.
            </p>
          </div>

          {providerState === "whisper_ws" ? (
            <div className="space-y-4">
              <FormField
                control={control}
                name="transcriber_ws_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">WebSocket URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="ws://127.0.0.1:8000/ws/transcribe"
                        className="bg-muted text-foreground border-input"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-muted-foreground">
                      WebSocket endpoint of your Whisper STT server. Leave blank to use the{" "}
                      <code className="text-xs">STT_WS_URL</code> environment variable.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">Language</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-muted text-foreground border-input">
                          <SelectValue placeholder="Select a language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getProviderLanguages().map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-muted-foreground">
                      Language hint passed to the Whisper server.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ) : providerState === "custom" ? (
            <div className="space-y-4">
              <FormField
                control={control}
                name="transcriber_base_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">
                      Base URL *
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://api.example.com"
                        className="bg-muted text-foreground border-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="transcriber_model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">Model *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="custom-model-name"
                        className="bg-muted text-foreground border-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="transcriber_api_key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">API Key</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Your API key (optional)"
                        className="bg-muted text-foreground border-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="transcriber_language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">
                      Language *
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="en"
                        className="bg-muted text-foreground border-input"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-muted-foreground">
                      Language code (e.g., en, es, fr) or "auto" for
                      auto-detection
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ) : (
            <div className="flex justify-between gap-4">
              <FormField
                control={control}
                name="language"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-foreground">Language</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-muted text-foreground border-input">
                          <SelectValue placeholder="Select a language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {providerState && sttProviders.providers[providerState] ? (
                          getProviderLanguages().map((lang) => (
                            <SelectItem key={lang.value} value={lang.value}>
                              {lang.label}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="hi">Hindi</SelectItem>
                            <SelectItem value="multi">Multi-language</SelectItem>
                            <SelectItem value="auto">Auto-detect</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-muted-foreground">
                      {enable_language_detection && (stt_model?.startsWith("nova-3") || stt_model?.startsWith("flux")) ? (
                        "🌐 Multilingual code-switching is enabled. The model supports seamless language transitions within conversations."
                      ) : (
                        <>
                          {providerState === "deepgram" && (
                            "Deepgram supports over 100 languages with high accuracy."
                          )}
                          {providerState === "groq" && (
                            "Groq Whisper models support multiple languages with auto-detection capabilities."
                          )}
                          {providerState === "sarvam" && (
                            "Sarvam AI specializes in Indic languages with high accuracy for Indian regional languages."
                          )}
                          {providerState === "openai" && (
                            "OpenAI supports GPT-4o transcription and Whisper models with support for 100+ languages and real-time capabilities."
                          )}
                          {providerState === "cartesia" && (
                            "Cartesia Ink-Whisper offers low-latency streaming with 80+ supported languages optimized for telephony audio."
                          )}
                          {providerState === "gnani" && (
                            "Gnani Vachana is tuned for Indian languages and 8kHz telephony audio. Choose Multi-Language for English/Hindi code-switching."
                          )}
                          {providerState === "smallestai" && (
                            "Smallest AI Pulse streams 21 languages including Indic ones. Choose Multi-Language to auto-detect the spoken language."
                          )}
                        </>
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {getProviderModels().length > 0 && (
            <FormField
              control={control}
              name="stt_model"
              render={({ field }) => {
                const providerModels = getProviderModels();
                const selectedModel = providerModels.find((m) => m.id === field.value);

                return (
                  <FormItem>
                    <FormLabel className="text-foreground">Model</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-muted text-foreground border-input">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {providerModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name}
                            {model.isDefault && " (Default)"}
                            {model.deprecated && " (Deprecated)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-muted-foreground">
                      {selectedModel?.description ||
                        "Choose the streaming model that matches your latency and accuracy needs."}
                      {selectedModel?.deprecated && " This model is deprecated. Prefer Ink-Whisper for best performance."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          {(providerState === "gnani" || providerState === "smallestai") && (
            <FormField
              control={control}
              name="stt_sample_rate"
              render={({ field }) => {
                const options =
                  sttProviders.providers[providerState]?.settings?.sampleRate?.options || [];
                const defaultRate =
                  sttProviders.providers[providerState]?.settings?.sampleRate?.default || 16000;

                return (
                  <FormItem>
                    <FormLabel className="text-foreground">Sample Rate</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={String(field.value || defaultRate)}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-muted text-foreground border-input">
                          <SelectValue placeholder="Select a sample rate" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {options.map((rate) => (
                          <SelectItem key={rate} value={String(rate)}>
                            {rate.toLocaleString()} Hz
                            {rate === 8000 && " (telephony)"}
                            {rate === defaultRate && " (Default)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-muted-foreground">
                      Sample rate of the audio sent to the transcriber. Use 8,000 Hz for PSTN
                      calls and 16,000 Hz for web calls.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          {providerState === "groq" && (
            <FormField
              control={control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground">Groq API Key</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Your Groq API key"
                      className="bg-muted text-foreground border-input"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="text-muted-foreground">
                    Required for Groq services. Get it from console.groq.com
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}


          <FormField
            control={control}
            name="backgroundDenoising"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base text-foreground">
                    Background Denoising Enabled
                  </FormLabel>
                  <FormDescription className="text-muted-foreground">
                    Filter background noise while the user is talking.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="useNumerals"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base text-foreground">
                    Use Numerals
                  </FormLabel>
                  <FormDescription className="text-muted-foreground">
                    Convert numbers from words to digits in transcription.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="enableFiltering"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base text-foreground">
                    Content Filtering
                  </FormLabel>
                  <FormDescription className="text-muted-foreground">
                    Enable content filtering for inappropriate speech.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Language Detection Toggle - only for Nova-3 and Flux models */}
          {providerState === "deepgram" && stt_model && (stt_model.startsWith("nova-3") || stt_model.startsWith("flux")) && (
            <FormField
              control={control}
              name="enable_language_detection"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base text-foreground">
                      Multi-Language Support
                    </FormLabel>
                    <FormDescription className="text-muted-foreground">
                      Enable multilingual code-switching to transcribe conversations with multiple languages seamlessly. 
                      {stt_model.startsWith("flux") ? " Enhanced for conversational AI with turn detection." : " Supports 10+ languages for Nova-3 models."}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="keyterms"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Keyterms</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter keywords separated by spaces. These will be used as key terms for transcription."
                    className="min-h-24 bg-muted text-foreground border-input"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="text-muted-foreground">
                  Enter keywords separated by spaces. These will be used as key
                  terms for transcription.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {(providerState === "deepgram" || providerState === "smallestai") && (
            <FormField
              control={control}
              name="endpointing_ms"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between mb-2">
                    <FormLabel className="text-foreground">Endpointing (ms)</FormLabel>
                    <div className="text-foreground">{field.value || 1000}ms</div>
                  </div>
                  <FormDescription className="text-muted-foreground mb-2">
                    Time in milliseconds to wait for silence before considering the user has finished speaking. Lower values make the assistant more responsive but may cut off speech.
                  </FormDescription>
                  <FormControl>
                    <Slider
                      min={100}
                      max={5000}
                      step={100}
                      defaultValue={[field.value || 1000]}
                      onValueChange={(vals) => field.onChange(vals[0])}
                      className="text-neon-purple"
                    />
                  </FormControl>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>100ms (responsive)</span>
                    <span>5000ms (patient)</span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
